import type { ReactNode } from 'react';
import styled from 'styled-components';
import { Button, ButtonVariants } from '../Button';
import { Stack } from '../Core';
import { FormControlSizes, ToggleWrapper } from '../Form';
import { IconName } from '../Icons';
import { Popover } from '../Popover/Popover';
import type { PopoverState } from '../Popover/types';
import { usePopoverState } from '../Popover/usePopoverState';
import { BlotterMenuWrapper } from './styles';

export interface BlotterTableTabFiltersMenuProps extends PopoverState {
  label: ReactNode;
  children: ReactNode;
  dataTestID?: string;
}

export function BlotterTableTabFiltersMenu({
  label,
  children,
  dataTestID = 'blotter-table-tab-filters-menu',
  ...popover
}: BlotterTableTabFiltersMenuProps) {
  return (
    <Popover {...popover}>
      <Button
        size={FormControlSizes.Small}
        variant={ButtonVariants.Default}
        startIcon={IconName.EyeShow}
        endIcon={IconName.ChevronDown}
        data-testid={dataTestID}
      >
        {label}
      </Button>
      <TabFiltersMenuWrapper data-testid="blotter-table-tab-filters-menu-content">{children}</TabFiltersMenuWrapper>
    </Popover>
  );
}

const TabFiltersMenuWrapper = styled(BlotterMenuWrapper)`
  gap: ${({ theme }) => theme.spacing16}px;

  button {
    text-align: left;
  }

  ${ToggleWrapper} {
    width: 100%;
    justify-content: space-between;
    text-align: left;
  }
`;

export function BlotterTableTabFiltersMenuGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack alignItems="stretch" w="100%" gap="spacing12" data-testid="blotter-tab-filters-group">
      <BlotterTableTabFiltersMenuGroupLabel data-testid="blotter-tab-filters-group-label">
        {label}
      </BlotterTableTabFiltersMenuGroupLabel>
      <Stack alignItems="stretch" w="100%" gap="spacing12">
        {children}
      </Stack>
    </Stack>
  );
}

const BlotterTableTabFiltersMenuGroupLabel = styled.div`
  color: ${({ theme }) => theme.colorTextSubtle};
  font-size: ${({ theme }) => theme.fontSizeTiny}rem;
  line-height: 1.2;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export function useBlotterTableTabFiltersMenu() {
  return usePopoverState({
    closeOnClickOutside: true,
    trigger: 'click',
    placement: 'bottom-start',
    usePortal: true,
  });
}
